import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

// Sends HEXA bundle forms through the same AJAX add + CartLinesUpdateEvent flow as
// product-form-component, so the existing cart drawer auto-opens instead of navigating to /cart.
// An inline guard in snippets/stylesheets.liquid queues submits made before this module loads.

/** @param {HTMLFormElement} form */
async function addBundle(form) {
  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('.hexa-bundle__cta'));
  if (button?.disabled || form.dataset.busy === 'true') return;
  form.dataset.busy = 'true';
  if (button) button.disabled = true;

  const formData = new FormData(form);
  formData.delete('return_to');
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
    }).then((res) => res.json());

    const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
    const cart = cartItems?.fetchCartData
      ? await cartItems.fetchCartData()
      : await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((res) => res.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'hexa-bundle',
        itemCount: quantity,
        sections: response.sections,
        didError: Boolean(response.status),
      },
    });

    if (response.status) {
      form.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
    }
  } catch (error) {
    deferred.reject(error);
  } finally {
    delete form.dataset.busy;
    if (button) button.disabled = false;
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('hexa-bundle__form')) return;
  event.preventDefault();
  addBundle(form);
});

// Cart upgrade card (snippets/hexa-cart-bundle-upgrade.liquid): swaps the single product line for
// the bundle. Adds the bundle first, then removes the single line, so a failed add leaves the cart
// untouched. The cart event is dispatched once the cart has settled, so every cart section
// re-renders from the real final state (also after a partial failure).

/** @param {HTMLButtonElement} button */
async function upgradeToBundle(button) {
  if (button.disabled) return;
  const error = button.closest('[data-hexa-cart-upgrade]')?.querySelector('.hexa-cart-upgrade__error');
  const { hexaUpgradeLine: lineKey, hexaUpgradeVariant: variantId } = button.dataset;
  if (!lineKey || !variantId) return;
  button.disabled = true;
  if (error) error.hidden = true;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
  /** @param {object} body */
  const post = (url, body) =>
    fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }).then((res) => res.json());

  /** @param {any} cart @param {Record<string, string>} sections @param {boolean} didError */
  const announce = (cart, sections, didError) =>
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [
          { merchandiseId: variantId, quantity: 1 },
          { id: lineKey, quantity: 0 },
        ],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'hexa-bundle-upgrade', itemCount: 1, sections, didError },
        }),
      })
    );

  let added = false;
  try {
    const addResponse = await post(Theme.routes.cart_add_url, { items: [{ id: Number(variantId), quantity: 1 }] });
    if (addResponse.status) throw new Error(addResponse.description || addResponse.message || 'Bundle add failed');
    added = true;

    const cart = await post(Theme.routes.cart_change_url, {
      id: lineKey,
      quantity: 0,
      sections: sectionIds,
      sections_url: window.location.pathname,
    });
    if (cart.status) throw new Error(cart.description || cart.message || 'Line removal failed');

    announce(cart, cart.sections, false);
    try {
      window.Shopify?.analytics?.publish?.('hexa_bundle_add', {
        bundle: button.dataset.hexaUpgradeBundle || '',
        location: 'cart_upgrade',
        product: button.dataset.hexaUpgradeProduct || '',
      });
    } catch (e) {}
  } catch (err) {
    console.error(err);
    if (added) {
      // The bundle went in but the single line stayed: re-render the cart from its real state.
      try {
        const [cart, sections] = await Promise.all([
          fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((res) => res.json()),
          fetch(`${window.location.pathname}?sections=${sectionIds}`).then((res) => res.json()),
        ]);
        announce(cart, sections, true);
      } catch (e) {}
    }
    if (error) error.hidden = false;
    button.disabled = false;
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('.hexa-cart-upgrade__cta');
  if (button instanceof HTMLButtonElement) upgradeToBundle(button);
});

window.hexaBundleCartReady = true;
const queued = window.hexaBundleCartQueue || [];
window.hexaBundleCartQueue = [];
queued.forEach((form) => addBundle(form));
